import React from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/SiteLayout";
import useScrollReveal from "@/hooks/useScrollReveal";
import HeroBadge from "@/components/HeroBadge";
import { ShieldCheck, Clock, Award, Users, Sparkles, Smile, Stethoscope, Heart, ArrowRight, MapPin, Phone, Star } from "lucide-react";

const REASONS = [
  { icon: ShieldCheck, t: "Trusted by 12,000+ families", d: "A members-only program with industry-leading renewal rates across Adajan." },
  { icon: Clock, t: "Same-day appointments", d: "Priority slots at our Adajan New Building — skip the usual wait every single time." },
  { icon: Award, t: "Only top-tier specialists", d: "Each consultant has 15+ years of clinical experience across India's top medical institutions." },
  { icon: Users, t: "Designed for families", d: "One card for up to 6 people. One manager to help everyone in your household." },
  { icon: Sparkles, t: "Premium experience", d: "Concierge support that feels more like luxury hospitality than traditional healthcare." },
  { icon: Smile, t: "Clear pricing", d: "No hidden fees, no surprises. What you see is exactly what you pay." },
  { icon: Stethoscope, t: "20+ specialties under one roof", d: "Cardiology, Neurology, Oncology, Pediatrics, Orthopedics — and more." },
  { icon: Heart, t: "Built for Adajan", d: "Local home-sample pickup, local emergency response, local relationship managers." },
];

const COMPARE = [
  ["Same-day OPD slots", true, false],
  ["Dedicated relationship manager", true, false],
  ["Diagnostics & Radiology savings", true, false],
  ["24×7 ambulance & ICU priority", true, false],
  ["Home sample collection in Adajan", true, false],
  ["Family coverage up to 6 members", true, false],
];

export default function WhyUs() {
  useScrollReveal();
  return (
    <SiteLayout>
      <section className="hero-grad relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-16 pb-12 relative">
          <div className="max-w-3xl reveal">
            <HeroBadge>Why Choose Us · <span className="text-[#A53A2A]">4.9 / 5 · 2,400+ Reviews</span></HeroBadge>
            <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-[#0F2540] leading-[1.04]">
              The NPC <span className="bg-gradient-to-r from-[#A53A2A] via-[#C89B3C] to-[#A53A2A] bg-clip-text text-transparent">difference.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-[#5C6B7A]">Eight reasons families in Adajan pick the Nirmal Privilege Card over any other healthcare program.</p>
          </div>
        </div>
      </section>

      {/* Reasons grid */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {REASONS.map((x, i) => (
            <div key={i} className="reveal lux-card rounded-2xl p-7" style={{ animationDelay: `${i * 70}ms` }}>
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#1E4F87] to-[#0F2540] text-white grid place-items-center"><x.icon className="h-6 w-6" /></div>
              <h3 className="mt-5 font-display text-lg font-bold text-[#0F2540]">{x.t}</h3>
              <p className="mt-2 text-sm text-[#5C6B7A] leading-relaxed">{x.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
        <div className="reveal max-w-2xl">
          <span className="overline">NPC vs. walk-in</span>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold text-[#0F2540]">Worth every rupee.</h2>
        </div>
        <div className="mt-10 reveal rounded-2xl bg-white border border-[#E1E8F0] overflow-hidden shadow-[0_24px_60px_-30px_rgba(15,37,64,0.18)]">
          <div className="grid grid-cols-3 bg-gradient-to-r from-[#0F2540] to-[#1E4F87] text-white text-sm">
            <div className="p-5 font-semibold">Benefit</div>
            <div className="p-5 text-center">NPC Member</div>
            <div className="p-5 text-center bg-[#A53A2A]/30">Walk-in</div>
          </div>
          {COMPARE.map(([l, vip, walk], i) => (
            <div key={l} className={`grid grid-cols-3 text-sm ${i % 2 ? "bg-[#F8FAFC]" : "bg-white"}`}>
              <div className="p-5 text-[#0F2540] font-medium">{l}</div>
              <div className="p-5 text-center text-[#1F7A3D] font-semibold">{vip ? "✓ Included" : "—"}</div>
              <div className="p-5 text-center text-[#5C6B7A]">{walk ? "Available" : "—"}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
        <div className="rounded-[2rem] mesh-dark p-10 lg:p-14 relative overflow-hidden reveal">
          <div className="absolute inset-0 dot-grid opacity-15 pointer-events-none" />
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <span className="overline text-[#C89B3C]">Ready when you are</span>
              <h3 className="mt-3 font-display text-3xl lg:text-4xl font-bold text-white">Activate your Nirmal Privilege Card in 5 minutes.</h3>
              <p className="mt-3 text-white/80 max-w-xl">From ₹2,000/year (incl. 18% GST). Digital card issued instantly.</p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end gap-3 flex-wrap">
              <Link to="/join" className="bg-white text-[#0F2540] px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-[#C89B3C] transition">Join NPC <ArrowRight className="h-4 w-4" /></Link>
              <a href="tel:+916357033301" className="border border-white/40 text-white px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-white/10 transition"><Phone className="h-4 w-4" /> +91-63570-33301</a>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
